import React from "react";
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import questionsDefaultData from "../assets/json/questions_data.json";
import {
  questionNumberingForSingleArm,
  questionNumberingForMultipleArm,
  questionNumberingForDoseFinding,
} from "../utils/WizardQuestionsNumbering";
import {
  multipleArmQuestionsOrder,
  doseFindingQuestionsOrder,
  singleArmQuestionsOrder,
  standardPkBlockQuestionsOrder,
  comparativePkBlockQuestionsOrder,
  relBioPkBlockQuestionsOrder,
  standardPdBlockQuestionsOrder,
  comparativePdBlockQuestionsOrder,
} from "../utils/WizardQuestionsOrder";
import styles from "../scss/pdfStyles";
import logoImage from "../assets/images/logos/TriloDocs-Logo-Horizontal-POSITIVE.png";

// Field labels shown in the app for object answers whose stored keys differ
// (see inputTextBoxes in BioavailabilityQuestions.js)
const fieldLabels = {
  min: "Lower bound",
  max: "Upper bound",
};

// Create Document Component
const WizardQuestionsPDF = ({ questionsData }) => {
  const formatNestedValue = (value, depth = 0, isArrayItem = false) => {
    // Handle null/undefined
    if (value === null) return "Not specified";
    if (value === undefined) return "Not specified";

    // Handle primitive types
    if (typeof value !== "object") {
      return String(value);
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return "N/A";
      // For simple arrays (all strings), format with bullet points
      if (value.every(item => typeof item === "string")) {
        return value.map(item => `• ${item}`).join("\n");
      }
      // For object arrays, use bullet points for all items
      return value
        .map(item => formatNestedValue(item, depth + 1, true))
        .join("\n");
    }

    // Handle objects
    const indent = "  ".repeat(depth);
    const entries = Object.entries(value);
    if (entries.length === 0) return "{}";

    // Special handling for objects with direct key-value pairs (like qPKmetaboliteNames)
    if (!isArrayItem && depth === 0) {
      return entries
        .map(([key, val]) => {
          const formattedValue = formatNestedValue(val, depth + 1, true);
          return `• ${fieldLabels[key] ?? key}: ${formattedValue}`;
        })
        .join("\n");
    }

    // Regular object handling for nested objects
    return entries
      .map(([key, val]) => {
        const formattedValue = formatNestedValue(val, depth + 1, isArrayItem);
        if (key === "valueHelper") {
          return `${indent}•  ${formattedValue}`;
        } else {
          // Add bullet point for array items and their nested properties
          const bullet = isArrayItem ? "• " : "";
          return `${indent}${bullet}${fieldLabels[key] ?? key}: ${formattedValue}`;
        }
      })
      .join("\n");
  };

  const formatAnswer = (answer, questionName = null) => {
    // Handle null/undefined
    if (answer === null)
      return <Text style={styles.answer}>Not specified</Text>;
    if (answer === undefined)
      return <Text style={styles.answer}>Not specified</Text>;

    // Handle arrays
    if (Array.isArray(answer)) {
      if (answer.length === 0) return <Text style={styles.answer}>N/A</Text>;

      // For simple arrays (all strings), format with bullet points
      if (answer.every(item => typeof item === "string")) {
        return (
          <>
            {answer.map((item, index) => (
              <Text key={index} style={styles.arrayItem}>
                • {item}
              </Text>
            ))}
          </>
        );
      }

      // For object arrays, use bullet points for all items and their properties
      return answer.map((item, index) => (
        <Text key={index} style={styles.arrayItem}>
          {formatNestedValue(item, 0, true)}
        </Text>
      ));
    }

    // Handle objects
    if (typeof answer === "object" && answer !== null) {
      const formattedObject = formatNestedValue(answer);
      return <Text style={styles.answer}>{formattedObject}</Text>;
    }

    // Special handling for qPkMultiDose field (including suffixed variants like qPkMultiDose_2, qPkMultiDoseComp_2)
    if (questionName && questionName.startsWith("qPkMultiDose")) {
      if (answer === "true" || answer === true) {
        answer = "Multiple Dose";
      } else if (answer === "false" || answer === false) {
        answer = "Single Dose";
      }
    } else if (
      questionName &&
      (questionName.startsWith("qPkDoseInteraction") ||
        questionName.startsWith("qPdDoseInteraction"))
    ) {
      // Special handling for qPkDoseInteraction and qPdDoseInteraction fields
      // (including suffixed variants like qPkDoseInteraction_2, qPdDoseInteraction_2)
      if (answer === "true" || answer === true) {
        answer = "Dose";
      } else if (answer === "false" || answer === false) {
        answer = "Subgroup/Drug";
      }
    } else if (questionName && questionName.startsWith("qPdComparative")) {
      // Special handling for qPdComparative field
      // (including suffixed variants like qPdComparative_2)
      if (answer === "true" || answer === true) {
        answer = "Comparative";
      } else if (answer === "false" || answer === false) {
        answer = "Non-comparative";
      }
    } else if (
      questionName &&
      questionsDefaultData.choices[questionName.replace(/_\d+$/, "")]
    ) {
      // Show the same option title the app shows (e.g. "Comparative" instead
      // of "Yes", "more than 40" instead of ">40"). Values are compared as
      // strings because the form stores booleans as "true"/"false".
      const match = questionsDefaultData.choices[
        questionName.replace(/_\d+$/, "")
      ].find(choice => String(choice.value) === String(answer));
      if (match) answer = match.title;
    } else {
      // Default boolean handling for other fields
      answer === "true"
        ? (answer = "Yes")
        : answer === "false"
          ? (answer = "No")
          : answer;
    }

    // Handle primitive types
    return <Text style={styles.answer}>{String(answer)}</Text>;
  };

  const getQuestionNumbering = wizardForm => {
    // Determine study type based on form values
    // Handle both string ("true"/"false") and boolean (true/false) values
    const isDoseFinding =
      wizardForm.qDoseFinding === "true" || wizardForm.qDoseFinding === true;
    const isComparative =
      wizardForm.qComparative === "true" || wizardForm.qComparative === true;

    // Journey type determination logic:
    // 1. If qDoseFinding is true → Dose-Finding (regardless of qComparative)
    // 2. Else if qComparative is true → Multiple Arm
    // 3. Else → Single Arm
    if (isDoseFinding) {
      return questionNumberingForDoseFinding;
    } else if (isComparative) {
      return questionNumberingForMultipleArm;
    } else {
      return questionNumberingForSingleArm;
    }
  };

  const getQuestionOrder = wizardForm => {
    // Determine study type based on form values
    // Handle both string ("true"/"false") and boolean (true/false) values
    const isDoseFinding =
      wizardForm.qDoseFinding === "true" || wizardForm.qDoseFinding === true;
    const isComparative =
      wizardForm.qComparative === "true" || wizardForm.qComparative === true;

    // Journey type determination logic:
    // 1. If qDoseFinding is true → Dose-Finding (regardless of qComparative)
    // 2. Else if qComparative is true → Multiple Arm
    // 3. Else → Single Arm
    if (isDoseFinding) {
      return doseFindingQuestionsOrder;
    } else if (isComparative) {
      return multipleArmQuestionsOrder;
    } else {
      return singleArmQuestionsOrder;
    }
  };

  const isValidAnswer = answer => {
    // Handle null/undefined
    if (answer == null) return false;

    // Handle arrays
    if (Array.isArray(answer)) {
      // Return false for empty arrays or arrays containing only null/empty values
      return (
        answer.length > 0 && !answer.every(item => item == null || item === "")
      );
    }

    // Handle objects
    if (typeof answer === "object") {
      // Return false for empty objects, or objects whose fields were all left
      // blank (e.g. qPkRelBioEstimateRange is saved as { min: null, max: null })
      return Object.values(answer).some(value => value != null && value !== "");
    }

    // Return false for empty strings
    if (answer === "") return false;

    // All other values (primitives) are considered valid
    return true;
  };

  const renderQuestionsAndAnswers = () => {
    const questions = [];
    const wizardForm = questionsData?.wizardForm || {};
    const questionNumbering = getQuestionNumbering(wizardForm);

    // Helper to check truthy form values (handles both string and boolean)
    const isTruthy = value => value === "true" || value === true;

    // Determine study flow booleans
    const isComparative = isTruthy(wizardForm.qComparative);
    const isSingleArm = !isComparative;
    const isDoseFinding = isTruthy(wizardForm.qDoseFinding);
    const isEfficacy = isTruthy(wizardForm.qIsEfficacy);
    const isPrimaryEfficacy = isTruthy(wizardForm.qPrimaryEfficacy);

    // ── Activator map ──────────────────────────────────────────────────
    // Mirrors the activator={…} props from the UI question components.
    // A question is included in the PDF only when its activator is true.
    const questionActivators = {
      // Base (Base.js)
      qStudyPhase: true,
      qStudyPhaseNames: isTruthy(wizardForm.qStudyPhase),
      qParticipantsName: true,
      qComparative: true,

      // Study Design – Single Arm
      ...(isSingleArm && {
        qDemPopulation: true,
        qCutOffsForDemographicsTables: true,
        qSingleArmMultipleColumns: true,
      }),

      // Study Design – Multiple Arm / Dose Finding
      ...(isComparative && {
        qDoseFinding: true,
        qDoseFindingSafety: isDoseFinding,
        qTgNamesDoseFinding:
          isDoseFinding && isTruthy(wizardForm.qDoseFindingSafety),
        qTgNames:
          (isDoseFinding && !isTruthy(wizardForm.qDoseFindingSafety)) ||
          !isDoseFinding,
        qTgNamesComparison: !isDoseFinding,
        qParticipantNumber: true,
        qDemPopulation: true,
        qCutOffsForDemographicsTables: true,
      }),

      // Efficacy – Single Arm
      ...(isSingleArm && {
        qIsEfficacy: true,
        qEfficacyAnalytes: isEfficacy,
        qEfficacyPopulations: isEfficacy,
        qEfficacyAnalysisTypeNames: isEfficacy,
        qEfficacyTimepointNames: isEfficacy,
        qPrimaryEfficacy: isEfficacy,
        qPrimaryEfficacyAnalytes: isEfficacy && isPrimaryEfficacy,
        qPrimaryEfficacyPopulation: isEfficacy && isPrimaryEfficacy,
        qPrimaryEfficacyAnalysisTypeNames: isEfficacy && isPrimaryEfficacy,
        qPrimaryEfficacyAnalytesComparison: isEfficacy && isPrimaryEfficacy,
        qPrimaryEfficacyTimepointNames: isEfficacy && isPrimaryEfficacy,
        qEfficacyKaplanMeierAnalytes: isEfficacy,
      }),

      // Efficacy – Multiple Arm / Dose Finding
      ...(isComparative && {
        qIsEfficacy: true,
        qEfficacyAnalytes: isEfficacy,
        qPrimaryEfficacyComparisonNames: isEfficacy,
        qEfficacyPopulations: isEfficacy,
        qEfficacyAnalysisTypeNames: isEfficacy,
        qEfficacyTimepointNames: isEfficacy,
        qPrimaryEfficacy: isEfficacy,
        qPrimaryEfficacyAnalytes: isEfficacy && isPrimaryEfficacy,
        qPrimaryEfficacyPopulation: isEfficacy && isPrimaryEfficacy,
        qPrimaryEfficacyAnalysisTypeNames: isEfficacy && isPrimaryEfficacy,
        qPrimaryEfficacyAnalytesComparison: isEfficacy && isPrimaryEfficacy,
        qPrimaryEfficacyTimepointNames: isEfficacy && isPrimaryEfficacy,
        qEfficacyKaplanMeierAnalytes: isEfficacy,
      }),

      // Safety – always visible for both flows
      qSafetyBaseline: true,
      qSafetyEndpoint: true,
      qCutOffsForAbnormalLabTables: true,
      qCutOffs: true,
      qCutOffsForAdverseEventsTables: true,

      // PK / PD toggle questions – always visible on their pages
      qPkStdEnabled: true,
      qPkCompEnabled: true,
      qPkRelBio: true,
      qPdStdEnabled: true,
      qPdCompEnabled: true,
    };

    // Helper function to add a question and its answer
    const addQuestion = (
      questionName,
      questionText,
      sectionLabel = null,
      blockNumber = null
    ) => {
      const answer = wizardForm[questionName];

      // Only add questions with valid answers
      if (isValidAnswer(answer)) {
        const questionNumber =
          questionNumbering[questionName] ||
          questionNumberingForDoseFinding[questionName] ||
          questionNumberingForSingleArm[questionName] ||
          questionNumberingForMultipleArm[questionName];

        // Build question label with section and block info
        let questionLabel = questionText;
        if (sectionLabel && blockNumber) {
          questionLabel = `[${sectionLabel} - Block ${blockNumber}] ${questionText}`;
        } else if (sectionLabel) {
          questionLabel = `[${sectionLabel}] ${questionText}`;
        }

        questions.push({
          questionName: questionName,
          number: questionNumber,
          sectionLabel: sectionLabel,
          blockNumber: blockNumber,
          element: (
            <View key={questionName} style={styles.questionSection}>
              <Text style={styles.question}>{questionLabel}</Text>
              {formatAnswer(answer, questionName)}
            </View>
          ),
        });
      }
    };

    // Helper function to add questions from a block with section and block number
    const addBlockQuestion = (
      questionName,
      questionText,
      sectionLabel,
      blockNumber,
      answer
    ) => {
      if (isValidAnswer(answer)) {
        const questionNumber =
          questionNumbering[questionName] ||
          questionNumberingForDoseFinding[questionName] ||
          questionNumberingForSingleArm[questionName] ||
          questionNumberingForMultipleArm[questionName];

        const questionLabel = `${questionText}`;

        questions.push({
          questionName: questionName,
          number: questionNumber,
          sectionLabel: sectionLabel,
          blockNumber: blockNumber,
          element: (
            <View
              key={`${questionName}-${sectionLabel}-${blockNumber}`}
              style={styles.questionSection}>
              <Text style={styles.question}>{questionLabel}</Text>
              {formatAnswer(answer, questionName)}
            </View>
          ),
        });
      }
    };

    // Extract questions from Standard PK Analysis blocks
    const isStdEnabled =
      wizardForm.qPkStdEnabled === "true" || wizardForm.qPkStdEnabled === true;
    if (isStdEnabled && Array.isArray(wizardForm.qPkStdAnalysis)) {
      wizardForm.qPkStdAnalysis.forEach((block, index) => {
        const blockNumber = index + 1;
        const sectionLabel = "Standard PK Analysis blocks";

        // All Standard PK block questions are always visible when the block exists
        standardPkBlockQuestionsOrder.forEach(questionName => {
          // WQ5b (qPkFiguresTimepoints) is only shown when WQ5a (qPkFiguresDescription) has a value
          if (
            questionName === "qPkFiguresTimepoints" &&
            !isValidAnswer(block.qPkFiguresDescription)
          ) {
            return;
          }

          const answer = block[questionName];

          if (isValidAnswer(answer)) {
            const questionText = questionsDefaultData.questions[questionName];
            if (questionText) {
              addBlockQuestion(
                questionName,
                questionText,
                sectionLabel,
                blockNumber,
                answer
              );
            }
          }
        });
      });
    }

    // Extract questions from Comparative PK Analysis blocks
    const isCompEnabled =
      wizardForm.qPkCompEnabled === "true" ||
      wizardForm.qPkCompEnabled === true;
    if (isCompEnabled && Array.isArray(wizardForm.qPkCompAnalysis)) {
      wizardForm.qPkCompAnalysis.forEach((block, index) => {
        const blockNumber = index + 1;
        const sectionLabel = "Comparative PK Analysis blocks";

        // All Comparative PK block questions are always visible when the block exists
        comparativePkBlockQuestionsOrder.forEach(questionName => {
          const answer = block[questionName];

          if (isValidAnswer(answer)) {
            const questionText = questionsDefaultData.questions[questionName];
            if (questionText) {
              addBlockQuestion(
                questionName,
                questionText,
                sectionLabel,
                blockNumber,
                answer
              );
            }
          }
        });
      });
    }

    // Extract questions from Relative Bioavailability PK Analysis blocks
    const isRelBioEnabled = wizardForm.qPkRelBio === "true";
    if (isRelBioEnabled && Array.isArray(wizardForm.qPkRelBioAnalyses)) {
      wizardForm.qPkRelBioAnalyses.forEach((block, index) => {
        const blockNumber = index + 1;
        const sectionLabel = "Relative Bioavailability PK Analysis blocks";

        // All Bioavailability block questions are always visible when the block exists
        relBioPkBlockQuestionsOrder.forEach(questionName => {
          const answer = block[questionName];

          if (isValidAnswer(answer)) {
            const questionText = questionsDefaultData.questions[questionName];
            if (questionText) {
              addBlockQuestion(
                questionName,
                questionText,
                sectionLabel,
                blockNumber,
                answer
              );
            }
          }
        });
      });
    }

    // Extract questions from Standard PD Analysis blocks
    // Support flat array, legacy object with blocks, and legacy qPdAnalyses array
    const stdPdData = wizardForm.qPdStdAnalyses;
    const isStdPdEnabled =
      wizardForm.qPdStdEnabled === "true" ||
      wizardForm.qPdStdEnabled === true ||
      stdPdData?.qPdStdEnabled === "true" ||
      stdPdData?.qPdStdEnabled === true;
    const stdPdBlocks = Array.isArray(stdPdData)
      ? stdPdData
      : (stdPdData?.blocks ??
        (Array.isArray(wizardForm.qPdAnalyses) ? wizardForm.qPdAnalyses : []));
    if (isStdPdEnabled && stdPdBlocks.length > 0) {
      stdPdBlocks.forEach((block, index) => {
        const blockNumber = block.qPdBlockNum || index + 1;
        const sectionLabel = "Standard PD blocks";

        // All Standard PD block questions are always visible when the block exists
        standardPdBlockQuestionsOrder.forEach(questionName => {
          const answer = block[questionName];

          if (isValidAnswer(answer)) {
            const questionText = questionsDefaultData.questions[questionName];
            if (questionText) {
              addBlockQuestion(
                questionName,
                questionText,
                sectionLabel,
                blockNumber,
                answer
              );
            }
          }
        });
      });
    }

    // Extract questions from Comparative PD Analysis blocks
    const isCompPdEnabled =
      wizardForm.qPdCompEnabled === "true" ||
      wizardForm.qPdCompEnabled === true ||
      wizardForm.qPdCompAnalyses?.qPdCompEnabled === "true" ||
      wizardForm.qPdCompAnalyses?.qPdCompEnabled === true;
    const compPdData = wizardForm.qPdCompAnalyses;
    const compPdBlocks = Array.isArray(compPdData)
      ? compPdData
      : (compPdData?.blocks ?? []);
    if (isCompPdEnabled && compPdBlocks.length > 0) {
      compPdBlocks.forEach((block, index) => {
        const blockNumber = block.qPdBlockNum || index + 1;
        const sectionLabel = "Comparative PD blocks";

        // Apply per-block activator logic matching ComparativePDQuestions.js:
        // qPdSummaryType === "true"  → show qPdParameters (with statistics)
        // qPdSummaryType === "false" → show qPdTreatmentGroups, qPdComparisonPairs,
        //                               qPdDoseInteraction, qPdTimepoints, qPdStatTables
        const summaryTypeIsTrue = isTruthy(block.qPdSummaryType);
        const summaryTypeIsFalse =
          block.qPdSummaryType === "false" || block.qPdSummaryType === false;

        const compPdBlockActivators = {
          qPdTable: true,
          qPdPopulation: true,
          qPdCompName: true,
          qPdSummaryType: true,
          qPdParameters: summaryTypeIsTrue,
          qPdTreatmentGroups: summaryTypeIsFalse,
          qPdComparisonPairs: summaryTypeIsFalse,
          qPdDoseInteraction: summaryTypeIsFalse,
          qPdTimepoints: summaryTypeIsFalse,
          qPdStatTables: summaryTypeIsFalse,
        };

        comparativePdBlockQuestionsOrder.forEach(questionName => {
          // Skip if activator is false for this question in this block
          if (!compPdBlockActivators[questionName]) return;

          const answer = block[questionName];

          if (isValidAnswer(answer)) {
            const questionText = questionsDefaultData.questions[questionName];
            if (questionText) {
              addBlockQuestion(
                questionName,
                questionText,
                sectionLabel,
                blockNumber,
                answer
              );
            }
          }
        });
      });
    }

    // Get the question order for the current form type
    const questionOrder = getQuestionOrder(wizardForm);

    // Track which questions have been added (to avoid duplicates from block processing)
    const addedQuestions = new Set();

    // Fields to skip (block arrays and internal fields)
    const fieldsToSkip = new Set([
      "qPkStdAnalysis",
      "qPkCompAnalysis",
      "qPkRelBioAnalyses",
      "qPdStdAnalyses",
      "qPdAnalyses",
      "qPdCompAnalyses",
      "_compUsesPrefixedControls",
    ]);

    // Helper function to add a question if it hasn't been added yet
    const addQuestionIfNotAdded = (questionName, questionText) => {
      if (
        !addedQuestions.has(questionName) &&
        !fieldsToSkip.has(questionName)
      ) {
        // Check activator – only include if the question was visible on the UI
        if (questionActivators[questionName] === false) return;
        // If the question is not in the activator map at all, skip it too
        // (it belongs to a different flow or was removed)
        if (!(questionName in questionActivators)) return;

        addedQuestions.add(questionName);
        addQuestion(questionName, questionText);
      }
    };

    // Process questions in the defined order
    // This ensures questions appear in the same order as on the Questions page
    questionOrder.forEach(questionName => {
      const questionText = questionsDefaultData.questions[questionName];
      if (questionText) {
        addQuestionIfNotAdded(questionName, questionText);
      }
    });

    // Sort questions to maintain order: use insertion index based on question order list
    // Blocks should appear right after their trigger question
    const questionOrderMap = new Map();
    questionOrder.forEach((qName, index) => {
      questionOrderMap.set(qName, index);
    });

    // Map section labels to their trigger questions
    const sectionTriggerMap = {
      "Standard PK Analysis blocks": "qPkStdEnabled",
      "Comparative PK Analysis blocks": "qPkCompEnabled",
      "Relative Bioavailability PK Analysis blocks": "qPkRelBio",
      "Standard PD blocks": "qPdStdEnabled",
      "Comparative PD blocks": "qPdCompEnabled",
    };

    // Calculate insertion index for each question
    questions.forEach(q => {
      if (q.sectionLabel) {
        // For block questions, place them right after their trigger question
        const triggerQuestion = sectionTriggerMap[q.sectionLabel];
        const triggerIndex = questionOrderMap.get(triggerQuestion) ?? Infinity;
        // Use trigger index + 0.1 for first block, + 0.2 for second block, etc.
        // This ensures blocks appear right after trigger, and multiple blocks are ordered correctly
        q.insertionIndex = triggerIndex + 0.1 + (q.blockNumber || 0) * 0.01;
      } else {
        // For regular questions, use their position in questionOrder
        const getBaseQuestionName = questionName => {
          if (!questionName) return "";
          // Remove numeric suffixes like _2, _3, etc.
          return questionName.replace(/_\d+$/, "");
        };
        const baseName = getBaseQuestionName(q.questionName || "");
        q.insertionIndex = questionOrderMap.get(baseName) ?? Infinity;
      }
    });

    // Sort by insertion index, then by block number within same insertion index, then by question order within block
    questions.sort((a, b) => {
      if (a.insertionIndex !== b.insertionIndex) {
        return a.insertionIndex - b.insertionIndex;
      }

      // If same insertion index (same block), sort by question order within block
      if (
        a.sectionLabel &&
        b.sectionLabel &&
        a.sectionLabel === b.sectionLabel
      ) {
        const aBlock = a.blockNumber || 0;
        const bBlock = b.blockNumber || 0;
        if (aBlock !== bBlock) {
          return aBlock - bBlock;
        }
        // Same block - sort by question order within block
        const getBaseQuestionName = questionName => {
          if (!questionName) return "";
          return questionName.replace(/_\d+$/, "");
        };
        const aBaseName = getBaseQuestionName(a.questionName || "");
        const bBaseName = getBaseQuestionName(b.questionName || "");

        // Get order within block
        let aBlockOrder = Infinity;
        let bBlockOrder = Infinity;
        if (a.sectionLabel === "Standard PK Analysis blocks") {
          aBlockOrder = standardPkBlockQuestionsOrder.indexOf(aBaseName);
          bBlockOrder = standardPkBlockQuestionsOrder.indexOf(bBaseName);
        } else if (a.sectionLabel === "Comparative PK Analysis blocks") {
          aBlockOrder = comparativePkBlockQuestionsOrder.indexOf(aBaseName);
          bBlockOrder = comparativePkBlockQuestionsOrder.indexOf(bBaseName);
        } else if (
          a.sectionLabel === "Relative Bioavailability PK Analysis blocks"
        ) {
          aBlockOrder = relBioPkBlockQuestionsOrder.indexOf(aBaseName);
          bBlockOrder = relBioPkBlockQuestionsOrder.indexOf(bBaseName);
        } else if (a.sectionLabel === "Standard PD blocks") {
          aBlockOrder = standardPdBlockQuestionsOrder.indexOf(aBaseName);
          bBlockOrder = standardPdBlockQuestionsOrder.indexOf(bBaseName);
        } else if (a.sectionLabel === "Comparative PD blocks") {
          aBlockOrder = comparativePdBlockQuestionsOrder.indexOf(aBaseName);
          bBlockOrder = comparativePdBlockQuestionsOrder.indexOf(bBaseName);
        }

        if (aBlockOrder !== bBlockOrder) {
          return aBlockOrder - bBlockOrder;
        }
      }

      // Fallback to question number
      return (
        a?.number?.localeCompare(b?.number, undefined, { numeric: true }) || 0
      );
    });

    // Group questions by section and add section headers
    const groupedElements = [];
    let currentSection = null;
    let currentBlock = null;

    questions.forEach((q, index) => {
      // Add section header if section changed
      if (q.sectionLabel && q.sectionLabel !== currentSection) {
        if (currentSection !== null) {
          // Add spacing after previous section
          groupedElements.push(
            <View key={`spacer-${index}`} style={{ marginBottom: 10 }} />
          );
        }
        groupedElements.push(
          <View
            key={`section-header-${q.sectionLabel}`}
            style={{ marginTop: 10, marginBottom: 5 }}>
            <Text style={{ fontSize: 14, fontWeight: "bold", color: "#333" }}>
              {q.sectionLabel}
            </Text>
          </View>
        );
        currentSection = q.sectionLabel;
        currentBlock = null;
      }

      // Add block header if block number changed within same section
      if (q.sectionLabel && q.blockNumber && q.blockNumber !== currentBlock) {
        groupedElements.push(
          <View
            key={`block-header-${q.sectionLabel}-${q.blockNumber}`}
            style={{ marginTop: 5, marginBottom: 3 }}>
            <Text style={{ fontSize: 12, fontWeight: "600", color: "#666" }}>
              Block {q.blockNumber}
            </Text>
          </View>
        );
        currentBlock = q.blockNumber;
      }

      groupedElements.push(q.element);
    });

    return groupedElements;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logoImage} style={styles.logo} />
        </View>
        {/* TODO: Uncomment and customize these styles if needed */}
        {/* <Text style={styles.company}>TriloDocs</Text>
        <Text style={styles.companySlogan}>AI-Enhanced Medical Writing</Text> */}
        <Text style={styles.title}>Wizard Questions and Answers</Text>
        <View style={styles.section}>{renderQuestionsAndAnswers()}</View>
      </Page>
    </Document>
  );
};

export default WizardQuestionsPDF;
